import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';

type VendorFullscreenDocument = Document & {
  webkitExitFullscreen?: () => Promise<void> | void;
  msExitFullscreen?: () => Promise<void> | void;
  mozCancelFullScreen?: () => Promise<void> | void;
};

type VendorFullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
  msRequestFullscreen?: () => Promise<void> | void;
  mozRequestFullScreen?: () => Promise<void> | void;
};

const MOTORQ_EMBED_URL =
  'https://conexio.metabaseapp.com/public/dashboard/7c7982dd-ebc8-4979-b47d-e416c9bbf4ec';

const MotorqPage: React.FC = () => {
  const theme = useTheme();
  const iframeContainerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, []);

  const toggleFullscreen = () => {
    const el = iframeContainerRef.current as VendorFullscreenElement | null;
    if (!el) return;
    const vdoc = document as unknown as VendorFullscreenDocument;

    if (!document.fullscreenElement) {
      if (el.requestFullscreen) el.requestFullscreen();
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
      else if (el.msRequestFullscreen) el.msRequestFullscreen();
      else if (el.mozRequestFullScreen) el.mozRequestFullScreen();
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (vdoc.webkitExitFullscreen) vdoc.webkitExitFullscreen();
      else if (vdoc.msExitFullscreen) vdoc.msExitFullscreen();
      else if (vdoc.mozCancelFullScreen) vdoc.mozCancelFullScreen();
    }
  };

  return (
    <Box pb={3}>
      <Box
        flexDirection="column"
        bgcolor={theme.palette.background.paper}
        sx={{ borderRadius: '14px', boxShadow: '0px 2px 16px 0px #1E315F1A' }}
      >
        <Box py="10px" px="20px" display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="subtitle1" style={{ fontWeight: 500, lineHeight: '26px' }}>
              Motorq Data Analysis
            </Typography>
            <Typography
              mt="2px"
              fontWeight={400}
              variant="subtitle2"
              lineHeight="20px"
              color={theme.palette.text.secondary}
            >
              Motorq fleet telemetry and data analysis dashboard
            </Typography>
          </Box>
        </Box>
        <Divider />

        <Box p="20px" pt="10px">
          <Box
            ref={iframeContainerRef}
            height="calc(100vh - 187px)"
            width="100%"
            sx={{
              overflow: 'hidden',
              position: 'relative',
              borderRadius: '14px',
              '&:fullscreen': { borderRadius: 0 },
            }}
          >
            <iframe
              width="100%"
              height="100%"
              src={MOTORQ_EMBED_URL}
              allowFullScreen
              allow="fullscreen"
              id="motorq-report-frame"
              title="Motorq Data Analysis"
              style={{ border: 'none', display: 'block' }}
            />
            <Box right={21} bottom={21} zIndex={1000} position="absolute">
              <Tooltip
                arrow
                title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                PopperProps={{
                  disablePortal: isFullscreen,
                  container: iframeContainerRef.current ?? undefined,
                }}
                slotProps={{
                  tooltip: {
                    sx: {
                      color: 'black',
                      whiteSpace: 'nowrap',
                      backgroundColor: 'white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    },
                  },
                  arrow: {
                    sx: {
                      color: 'white',
                    },
                  },
                }}
              >
                <IconButton
                  onClick={toggleFullscreen}
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                  }}
                >
                  {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MotorqPage;
